"use client";

import { createPortal } from "react-dom";
import { useMemo, useRef, memo } from "react";
import { useNavbar } from "./navbar.context";
import { useNavbarMenu } from "./hooks/useNavbarMenu";
import { easeOutExpo, easeOutQuad } from "../../utils/easings";
import { useIsomorphicLayoutEffect } from "../../hooks";

import { navbarMenuStyles, navbarMenuTriggerStyles } from "./styles/navbar-menu.styles";
import type { NavbarMenuProps } from "./types/navbar-menu.types";

import { AnimatePresence, motion, useAnimate } from "motion/react";
import { IconChevronDown } from "../../icons";

const ANIMATION_DELAY = 10;
const SPRING_BOUNCE = 0.2;
const SPRING_DURATION = 0.5;
const HEIGHT_DURATION = 0.55;

export const NavbarMenu: React.FC<NavbarMenuProps> = memo((props) => {
  const { label, children, id, onPointerLeave, ...rest } = props;

  const { portalRef, value, mounted } = useNavbar();

  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentRef = useRef<HTMLUListElement>(null);

  const isOpen = useMemo(() => value === id, [value, id]);

  const [scope, animate] = useAnimate();

  const { getMenuPosition, setMenuPosition, handleToggleMenu, openOnPointerEnter, closeOnPointerLeave, handleKeyDown } =
    useNavbarMenu({
      triggerRef,
      contentRef,
      portalRef,
      id,
      onPointerLeave,
    });

  useIsomorphicLayoutEffect(() => {
    if (!isOpen) return;

    const pos = getMenuPosition();

    if (!pos) return;

    const timeoutId = setTimeout(() => {
      if (!portalRef?.current) return;

      const shouldAnimate = portalRef.current.dataset.shouldAnimate === "true";

      animate(
        portalRef.current,
        {
          x: pos.x,
          height: pos.height,
          z: 0,
        },
        {
          type: "spring",
          bounce: SPRING_BOUNCE,
          duration: shouldAnimate ? SPRING_DURATION : 0,
          height: {
            duration: shouldAnimate ? HEIGHT_DURATION : 0,
            ease: easeOutExpo,
          },
        },
      );
    }, ANIMATION_DELAY);

    return () => clearTimeout(timeoutId);
  }, [isOpen, setMenuPosition, getMenuPosition, animate, portalRef]);

  return (
    <li ref={scope} role="presentation" {...rest} onPointerLeave={closeOnPointerLeave}>
      <button
        ref={triggerRef}
        id={id}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-controls={`${id}-content`}
        className={navbarMenuTriggerStyles({ isOpen })}
        onClick={handleToggleMenu}
        onKeyDown={handleKeyDown}
        onPointerEnter={openOnPointerEnter}
      >
        <span>{label}</span>
        <IconChevronDown size="xs" />
      </button>

      {mounted
        ? createPortal(
            <AnimatePresence>
              {isOpen ? (
                <motion.ul
                  key="content"
                  ref={contentRef}
                  id={`${id}-content`}
                  role="menu"
                  aria-labelledby={id}
                  className={navbarMenuStyles()}
                  initial={{ opacity: 0, zIndex: 0 }}
                  animate={{ opacity: 1, zIndex: 1 }}
                  exit={{ opacity: 0, zIndex: 0 }}
                  transition={{ duration: 0.3, ease: easeOutQuad }}
                >
                  {children}
                </motion.ul>
              ) : null}
            </AnimatePresence>,
            portalRef?.current ?? document.body,
          )
        : null}
    </li>
  );
});

NavbarMenu.displayName = "NavbarMenu";
